<script lang="ts">
    import { onMount } from "svelte";

    import SVGIcon from "$/components/SVGIcon.svelte";

    import { ActiveTopicStore, ActiveTopic } from "$/stores/extension/ActiveTopicStore";
    import { TokenStore } from "$/stores/TokenStore";
    import {
        checkAndGenerateSocialMediaURL,
        toggleSocialMediaPostSaveState,
        debounce,
    } from "$/utils/Utils";
    import { CardTitleUtils } from "../card/CardTitle/CardTitleUtils";
    import { AppStore } from "$/stores/AppStore";
    import { v4 as uuid } from "uuid";
    import {ExtensionStore} from "$/stores/ExtensionStore";

    let saveButton: HTMLButtonElement;
    let observer: MutationObserver;
    let initialLoad = false;
    export let editRole: number;

    // check if theres email-collection-tooltip-id id
    $: isLoggedReddit =
        document.getElementById("email-collection-tooltip-id") !== null
            ? true
            : false;

    // function to create the "Save Post" button
    const createSaveButton = (post) => {
        // clone our button
        const redditSaveButton = saveButton.cloneNode(
            true,
        ) as HTMLButtonElement;

        // remove the hidden class from the button
        redditSaveButton.classList.remove("cw-save-hide");

        redditSaveButton.addEventListener("click", async (event) => {
            event.stopPropagation();
            event.preventDefault();

            toggleSocialMediaPostSaveState(redditSaveButton, true);

            // handle saving the reddit post as a snippet
            // Your code here to save the reddit post as a snippet
            let data = JSON.parse(redditSaveButton.dataset.postData);
            let snippet = {
                id: uuid(),
                content: data.postContent ?? "No content",
                nameSuggestion: data.title,
                comments: [],
                url: checkAndGenerateSocialMediaURL("reddit", data.url),
                createdBy: $TokenStore?.user,
                createdOn: new Date().getTime(),
            };

            ExtensionStore.setOpened(true);

            ActiveTopicStore.createSnippet(snippet);

            // set focus on the newly created snippet
            AppStore.setFocused({ snippetId: snippet.id });

            // hide the loading spinner
            toggleSocialMediaPostSaveState(redditSaveButton, false);
        });

        redditSaveButton.dataset.postData = JSON.stringify(post);
        return redditSaveButton;
    };

    // function to extract data from a post element
    const extractPostData = (element: HTMLElement) => {
        // Extract permalink, title, and text-body
        const permalink = element.getAttribute("permalink");
        // find element with slot="title" and get the text content
        const titleElement = element.querySelector('[slot="title"]');
        const title = titleElement?.textContent?.trim() ?? "No title";
        // find element with data-post-click-location="text-body" and get the text content
        const textBodyElement = element.querySelector(
            '[data-post-click-location="text-body"]',
        );
        const textBody = textBodyElement?.textContent?.trim() ?? "";
        // remove any links from the text body and remove View full post line if it exists
        const textBodyWithoutLinks = textBody
            ?.replace(/(https?:\/\/[^\s]+)/g, "")
            .replace(/View full post/g, "")
            .trim();

        let postContent = textBodyWithoutLinks;
        // if theres no text body, set post content to title
        if (!textBodyWithoutLinks || textBodyWithoutLinks === "") {
            postContent = `<b>${title}</b><br>No content`;
        }

        // check if postContent has more than 10k characters
        // if it does, remove trim it untill it has 10k - 3 characters and add "..." to the end
        if (postContent.length > 10000) {
            postContent = postContent.substring(0, 10000 - 3) + "...";
        }

        return {
            url: `https://www.reddit.com${permalink}`,
            postContent: postContent,
            title: title,
        };
    };

    // function to extract data from a post element
    const extractPostDataLoggedIn = (element: HTMLElement) => {
        // find data-click-id="body" element and get href attribute
        const permalink =
            element
                .querySelector('[data-click-id="body"]')
                ?.getAttribute("href") ?? "";
        // find element with data-adclicklocation="title" and get the text content that is inside h3 or h1 element
        const titleElement = element
            .querySelector('[data-adclicklocation="title"]')
            ?.querySelector("h3, h1") as HTMLElement;
        const title = titleElement?.textContent?.trim() ?? "No title";
        // find element with data-adclicklocation="media" and get the text content
        const textBodyElement = element.querySelector(
            '[data-adclicklocation="media"]',
        ) as HTMLElement;
        // get all p elements inside textBodyElement and add a new line between them
        const textBodyP = textBodyElement?.querySelectorAll("p") as
            | NodeListOf<HTMLElement>
            | undefined;

        if (!textBodyP?.length)
            return {
                url: `https://www.reddit.com${permalink}`,
                postContent: `<b>${title}</b><br>No content`,
            };

        const textBodyNewLines = Array.from(textBodyP)
            .map((p) => p.textContent?.trim())
            .join("<br>");

        // get text content of textBodyNewLines
        const textBody = textBodyNewLines?.trim();

        let postContent = textBody;
        if (!textBody || textBody === "") {
            postContent = `<b>${title}</b><br>No content`;
        }

        // check if postContent has more than 10k characters
        // if it does, remove trim it untill it has 10k - 3 characters and add "..." to the end
        if (postContent.length > 10000) {
            postContent = postContent.substring(0, 10000 - 3) + "...";
        }

        return {
            url: `https://www.reddit.com${permalink}`,
            postContent: postContent,
            title: title,
        };
    };

    // function to extract data from a post element
    const extractPostDataComment = async (element: HTMLElement) => {
        let permalink = isLoggedReddit
            ? // get comment url from timestamp
              (element
                  .querySelector('[data-testid="comment_timestamp"]')
                  ?.getAttribute("href")
                  .split("?")[0] ?? "")
            : // get permalink from element
              (`https://www.reddit.com${element.getAttribute("permalink")}` ??
              "");

        // find element with data-testid="comment" and get the text content
        const textBodyElement = isLoggedReddit
            ? (element.querySelector('[data-testid="comment"]') as HTMLElement)
            : (element.querySelector('[slot="comment"]') as HTMLElement);

        // get all p elements inside textBodyElement and add a new line between them
        const textBodyP = textBodyElement?.querySelectorAll("p") as
            | NodeListOf<HTMLElement>
            | undefined;

        let titleSuggestion = CardTitleUtils.generateTitle(document);

        if (!textBodyP?.length)
            return {
                url: `${permalink}`,
                postContent: `No content`,
                title: titleSuggestion,
            };

        const textBodyNewLines = Array.from(textBodyP)
            .map((p) => p.textContent?.trim())
            .join("<br>");

        // get text content of textBodyNewLines
        const textBody = textBodyNewLines?.trim() ?? "No content";
        // combine title and text body and add a new line between them
        let postContent = `${textBody ?? "No content"}`;

        // check if postContent has more than 10k characters
        // if it does, remove trim it untill it has 10k - 3 characters and add "..." to the end
        if (postContent.length > 10000) {
            postContent = postContent.substring(0, 10000 - 3) + "...";
        }

        return {
            url: `${permalink}`,
            postContent: postContent,
            title: titleSuggestion,
        };
    };

    const addSaveButtonToPosts = () => {
        const postElements = document.querySelectorAll("shreddit-post");

        // check if the post already has a "Save Post" button
        postElements.forEach((element: HTMLElement) => {
            if (!element.querySelector(".cw-save-btn")) {
                const post = extractPostData(element);
                const saveButton = createSaveButton(post);
                // add position relative to element
                element.style.position = "relative";
                // append the save button to the post
                element.append(saveButton);
                // remove isLoggedOutComment class from post save button
                saveButton.classList.remove("isLoggedOutComment");
            }
        });
    };

    const addSaveButtonToPostsOpenPost = () => {
        const postElements = document.querySelectorAll("shreddit-post");
        // get elements with Comment class if user is logged in and if not get shreddit-comment elements
        const commentElements = isLoggedReddit
            ? document.querySelectorAll(".Comment")
            : document.querySelectorAll("shreddit-comment");
        // check if the post already has a "Save Post" button
        postElements.forEach((element: HTMLElement) => {
            if (!element.querySelector(".cw-save-btn")) {
                const post = isLoggedReddit
                    ? extractPostDataLoggedIn(element)
                    : extractPostData(element);
                const saveButton = createSaveButton(post);
                // set element position to relative
                element.style.position = "relative";

                // add isCommentPost class to save button
                saveButton.classList.add("isCommentPost");

                // append the save button to the post
                element.append(saveButton);
            }
        });

        commentElements.forEach(async (element: HTMLElement) => {
            if (!element.querySelector(".cw-save-btn")) {
                const post = await extractPostDataComment(element);
                const saveButton = createSaveButton(post);
                // add isComment class to save button
                saveButton.classList.add("isComment");

                // append the save button to the post
                if (isLoggedReddit) element.append(saveButton);
                // get slot="comment" element and append button to its parent and set parent to relative
                else {
                    const cmntParent =
                        element.querySelector('[slot="comment"]');
                    if (cmntParent) cmntParent.append(saveButton);
                }
            }
        });
    };

    // function to add "Save Post" buttons to posts
    const addSaveButtonToPostsLoggedIn = () => {
        // find data-testid="post-container" elements
        const postElements = document.querySelectorAll(
            '[data-testid="post-container"]',
        );
        // check if the post already has a "Save Post" button
        postElements.forEach((element: HTMLElement) => {
            if (!element.querySelector(".cw-save-btn")) {
                const post = extractPostDataLoggedIn(element);
                const saveButton = createSaveButton(post);

                // find parent of data-test-id="comments-page-link-num-comments"
                const commentsLinkParent = element.querySelector(
                    '[data-test-id="comments-page-link-num-comments"]',
                )?.parentElement;

                // if commentsLinkParent exists, insert the save button in it
                if (commentsLinkParent)
                    commentsLinkParent.insertBefore(
                        saveButton,
                        commentsLinkParent.firstChild,
                    );
            }
        });
    };

    // function to check the current URL and add Save Post buttons if needed
    const checkAndAddSaveButtons = () => {
        const currentUrl = window.location.href;
        if (currentUrl.includes("reddit.com")) {
            // if user is logged loggedout reddit
            let postElements = [];
            if (isLoggedReddit)
                document.querySelectorAll('[data-testid="post-container"]');
            else document.querySelectorAll("shreddit-post");

            if (!postElements.length) {
                setTimeout(() => {
                    isLoggedReddit
                        ? addSaveButtonToPostsLoggedIn()
                        : addSaveButtonToPosts();
                }, 1000);
            } else {
                isLoggedReddit
                    ? addSaveButtonToPostsLoggedIn()
                    : addSaveButtonToPosts();
            }
        }

        // continue observing changes in the document body
        observer?.observe(document.body, {
            attributes: false,
            childList: true,
            subtree: true,
        });
    };

    // function to remove all "Save Post" buttons
    const removeSaveButtons = () => {
        const saveButtons = document.querySelectorAll(".cw-save-btn");
        saveButtons.forEach((button: HTMLElement) => button.remove());

        // stop observing changes in the document body if observer exists
        observer?.disconnect();
    };

    onMount(() => {
        // create observer listener for new elements shreddit-post
        observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "childList" && editRole >= 3)
                    if (
                        window.location.href.includes("reddit.com") &&
                        window.location.href.includes("/comments/") &&
                        document.querySelector("shreddit-comment-tree")
                    ) {
                        // if we are on comments page, and comments have rendered, add save buttons to posts
                        // Debounce to save performance
                        debounce(addSaveButtonToPostsOpenPost, 1000)();
                    } else {
                        isLoggedReddit
                            ? addSaveButtonToPostsLoggedIn()
                            : addSaveButtonToPosts();
                    }
            });
        });

        // initial check for posts on page load
        if (editRole >= 3) {
            checkAndAddSaveButtons();
        }
        initialLoad = true;
    });

    // if user has edit role >= 3 and initial load (component mounted), add "Save Post" buttons else remove them
    $: initialLoad && editRole >= 3
        ? checkAndAddSaveButtons()
        : removeSaveButtons();
</script>

{#if window.location.href.includes("reddit.com")}
    <span class="isCommentPost isComment hidden"></span>
    <button
        class="cw-save-hide cw-save-btn cw-save-btn-reddit"
        title="Save as Snippet"
        bind:this={saveButton}
    >
        <div class="cw-save__loading-spinner reddit" style="display: none;">
            <SVGIcon
                name="spinner"
                color="#666666"
                height="100%"
                size="100%"
                viewbox="0 0 24 24"
                strokeWidth="2"
            />
        </div>
        <div class="cw-save__icon reddit">
            <SVGIcon
                name="save"
                height="100%"
                size="100%"
                viewbox="-4 -4 36 36"
                strokeWidth="0.5"
            />
        </div>
    </button>
{/if}

<style>
    @import "../../extension/external.css";
</style>
